import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronLeft, Download, Loader2, Upload } from 'lucide-react'
import { Button } from '@workspace/ui/components/Button'
import MenuDropdown from '@/shared/components/MenuDropdown'
import { useTranslation } from 'react-i18next'
import { toast } from '@workspace/ui/components/Sonner'
import { TemplateForm, type TemplateFormHandle } from '@/shared/components/form/TemplateForm'
import { api, type ProjectTemplateCreate } from '@/shared/lib/api'
import type { TreeNodeData } from '@/shared/types'
import { z } from 'zod'
import { treeNodeToTemplateFolder } from '@/shared/utils/template.utils'
import { useImportTemplate } from '@/shared/hooks/templates/useImportTemplate'
import React, { useRef, useState } from 'react'

const templateSearchSchema = z.object({
    id: z.string().optional(),
})

export const Route = createFileRoute('/templates/new')({
    component: CreateTemplatePage,
    validateSearch: templateSearchSchema,
})

function CreateTemplatePage() {
    const { t } = useTranslation()
    const importMutation = useImportTemplate()
    const router = useRouter()
    const search = Route.useSearch()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<TemplateFormHandle>(null)
    const [importedStructure, setImportedStructure] = useState<TreeNodeData[] | undefined>(undefined)

    // Default structure for new templates
    const defaultStructure: TreeNodeData[] = [
        {
            id: '1',
            type: 'folder',
            name: 'A. CHUẨN BỊ THỰC HIỆN DỰ ÁN',
            isLocked: false,
            children: [
                {
                    id: '1-1',
                    type: 'folder',
                    name: 'A.1 Nghiên cứu dự án',
                    isLocked: false,
                    children: [
                        {
                            id: 'BBTĐ-NC003',
                            type: 'document',
                            name: 'Báo cáo tiền độ nghiên cứu',
                            documentCode: 'BBTĐ-NC003',
                            classificationLevel: 'NORMAL',
                            isLocked: false,
                        },
                    ],
                },
                {
                    id: '1-2',
                    type: 'folder',
                    name: 'A.2 Quy hoạch',
                    isLocked: false,
                    children: [
                        {
                            id: 'BBTĐ-KH003',
                            type: 'document',
                            name: 'Báo cáo kế hoạch',
                            documentCode: 'BBTĐ-KH003',
                            classificationLevel: 'NORMAL',
                            isLocked: false,
                        },
                    ],
                },
            ],
        },
        {
            id: '2',
            type: 'folder',
            name: 'B. TRIỂN KHAI DỰ ÁN',
            isLocked: false,
            children: [
                {
                    id: '2-1',
                    type: 'folder',
                    name: 'B.1 Giải phóng mặt bằng',
                    isLocked: false,
                    children: [],
                },
            ],
        },
    ]

    // Fetch template data if duplicating
    const { data: templateData, isLoading } = useQuery({
        queryKey: ['project-template', search.id],
        queryFn: () => api.projectTemplate.findOne(search.id!),
        enabled: !!search.id,
        retry: 1,
    })

    // Create template mutation
    const createMutation = useMutation({
        mutationFn: async (payload: ProjectTemplateCreate) => {
            return await api.projectTemplate.create(payload)
        },
        onSuccess: (template, variables) => {
            toast.success({
                title: t('common.status.success'),
                description: variables.isDraft
                    ? t('templates.create.draftSaved', 'Đã lưu nháp')
                    : t('templates.create.createSuccess'),
            })
            router.navigate({ to: '/templates' })
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || ''
            const errorCode = error.response?.data?.code || error.response?.data?.error || ''

            // Check for duplicate template name
            if (
                errorMessage.includes('name') ||
                errorMessage.includes('duplicate') ||
                errorMessage.includes('trùng') ||
                errorMessage.includes('already exists') ||
                errorCode === 'DUPLICATE_NAME' ||
                errorCode === 'TEMPLATE_NAME_EXISTS'
            ) {
                toast.error({
                    title: t('common.status.error'),
                    description: t('templates.create.duplicateNameError'),
                })
                return
            }

            // Check for duplicate folder names
            if (
                errorMessage.includes('folder') ||
                errorMessage.includes('thư mục') ||
                errorCode === 'DUPLICATE_FOLDER_NAME'
            ) {
                toast.error({
                    title: t('common.status.error'),
                    description: t('templates.form.validation.duplicateFolderNames'),
                })
                return
            }

            // Check for duplicate document names
            if (
                errorMessage.includes('document name') ||
                errorMessage.includes('tên tài liệu') ||
                errorCode === 'DUPLICATE_DOCUMENT_NAME'
            ) {
                toast.error({
                    title: t('common.status.error'),
                    description: t('templates.form.validation.duplicateDocumentNames'),
                })
                return
            }

            // Check for duplicate document codes
            if (
                errorMessage.includes('document code') ||
                errorMessage.includes('mã tài liệu') ||
                errorCode === 'DUPLICATE_DOCUMENT_CODE'
            ) {
                toast.error({
                    title: t('common.status.error'),
                    description: t('templates.form.validation.duplicateDocumentCodes'),
                })
                return
            }

            // Generic error
            toast.error({
                title: t('common.status.error'),
                description: t('templates.create.createError'),
            })
            console.error('Create template error:', error)
        },
    })

    const handleSaveDraft = () => {
        formRef.current?.setValue('isDraft', true)
        formRef.current?.submit()
    }

    const handleImportClick = () => {
        fileInputRef.current?.click()
    }

    const handleDownloadTemplate = () => {
        const link = document.createElement('a')
        link.href = '/files/Template_pmsvb.xlsx'
        link.download = 'Template_pmsvb.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        // Reset input so the same file can be re-selected
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        importMutation.mutate(file, {
            onSuccess: tree => {
                setImportedStructure(tree)
                toast.success({
                    title: t('common.status.success'),
                    description: t('templates.create.importSuccess'),
                })
            },
            onError: error => {
                toast.error({
                    title: t('common.status.error'),
                    description: t('templates.create.importStructureError'),
                })
                console.error('Import template error:', error)
            },
        })
    }

    const handleSubmit = (formData: {
        name: string
        description?: string
        regionId?: string[]
        areaId?: string[]
        structure: { folders: TreeNodeData[] }
        isDraft?: boolean
    }) => {
        const payload: ProjectTemplateCreate = {
            name: formData.name.trim(),
            description: formData.description || formData.name.trim(),
            projectType: 'general',
            regions: formData.regionId || [],
            areas: formData.areaId || [],
            structure: {
                folders: formData.structure.folders.map((node, index) => treeNodeToTemplateFolder(node, '', index)),
            },
            settings: {
                priority: 'medium',
                autoAssign: false,
            },
            isActive: true,
            isDraft: formData.isDraft,
        }

        createMutation.mutate(payload)
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
        )
    }

    const isImporting = importMutation.isPending

    return (
        <div className="relative">
            {/* Import in-progress overlay */}
            {isImporting && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-lg bg-background/80 backdrop-blur-sm">
                    <Loader2 className="size-10 animate-spin text-primary" />
                    <p className="text-sm font-medium text-muted-foreground">
                        {t('templates.create.importing', 'Đang xử lý tệp...')}
                    </p>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="icon"
                        className="border-border-subtle"
                        onPress={() => router.history.back()}
                    >
                        <ChevronLeft className="size-5" />
                    </Button>
                    <h1 className="text-2xl font-bold">{t('templates.create.title')}</h1>
                </div>
                <div className="flex gap-2 max-lg:grid max-lg:grid-cols-3 max-lg:w-full">
                    <MenuDropdown
                        className="max-lg:w-full"
                        actions={[
                            {
                                label: t('templates.list.uploadTemplate'),
                                onAction: handleImportClick,
                                icon: <Upload className="size-4" />,
                            },
                            {
                                label: t('templates.list.downloadTemplate'),
                                onAction: handleDownloadTemplate,
                                icon: <Download className="size-4" />,
                            },
                        ]}
                    >
                        <Button variant="outline" size="lg" isDisabled={isImporting}>
                            {isImporting ? (
                                <>
                                    <Loader2 className="size-4 mr-1 animate-spin" />
                                    {t('templates.create.importing', 'Đang nhập...')}
                                </>
                            ) : (
                                <>
                                    {t('templates.list.uploadMenu')}
                                    <ChevronDown className="size-4 ml-1" />
                                </>
                            )}
                        </Button>
                    </MenuDropdown>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,.xml"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <Button
                        variant="outline"
                        size="lg"
                        onPress={handleSaveDraft}
                        isDisabled={createMutation.isPending || isImporting}
                    >
                        {t('templates.create.saveDraft')}
                    </Button>
                    <Button
                        variant="default"
                        size="lg"
                        type="submit"
                        form="template-form"
                        onPress={() => formRef.current?.setValue('isDraft', false)}
                        isDisabled={createMutation.isPending || isImporting}
                    >
                        {t('templates.create.createButton')}
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <TemplateForm
                ref={formRef}
                defaultValues={templateData}
                defaultStructure={!search.id ? defaultStructure : undefined}
                importedStructure={importedStructure}
                onSubmit={handleSubmit}
                isSubmitting={createMutation.isPending}
            />
        </div>
    )
}
